import { observer, useExpressionScope } from '@formily/react';
import { Entity, useDeepEffect, useEntityConfig, useInitStore, useStore } from '@yimoka/react';
import { IAny, IAnyObject, IOptions, isVacuous } from '@yimoka/shared';
import { autorun, IEntityConfig, ISchema, IStore, IStoreAPI } from '@yimoka/store';

import { Alert, Modal, notification, Select, Tabs, theme } from 'antd';
import { union } from 'lodash-es';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Drawer } from '../feedback/drawer';
import { useLocaleComponent } from '../hooks/use-locale';

const { useToken } = theme;

export interface I18nEditorProps {
  store?: IStore;
  config?: IEntityConfig
  fieldMap: Record<string, string>;
  langAPI?: IStoreAPI;
  readOnly?: boolean;
  InContent?: React.FC<{ value?: IAnyObject, onChange?: (v?: IAnyObject) => void }>;
  OutContent?: React.FC<{ value?: IAnyObject }>;
  inSchema?: ISchema;
  outSchema?: ISchema;
  inProps?: Record<string, string>;
  outProps?: Record<string, string>;
}

/** 多语言编辑器负责按语言编辑字段映射，并在非只读模式下原子写回 Store。 */
export const I18nEditor = observer(({ config, store, readOnly, fieldMap, langAPI, InContent, OutContent, inSchema, outSchema, inProps, outProps }: I18nEditorProps) => {
  const { token } = useToken();
  const locale = useLocaleComponent('I18nEditor');
  const scope = useExpressionScope();
  const curConfig = config ?? scope?.$config;
  const langStore = useInitStore({ defaultValues: {} });
  const curLangOptions = useI18nLangOptions(langAPI, curConfig);
  const curStore = useStore(store);
  const values = curStore?.values;

  const [addLang, setAddLang] = useState('');
  const [isAddLang, setIsAddLang] = useState(false);
  const [langs, setLangs] = useState<string[]>([]);
  const curI18nFieldMap = useMemo(() => fieldMap ?? curConfig?.i18nFieldMap ?? {}, [fieldMap, curConfig]);
  const curI18nInSchema = useMemo(() => inSchema ?? curConfig?.i18nInSchema, [inSchema, curConfig]);
  const curI18nOutSchema = useMemo(() => outSchema ?? curConfig?.i18nOutSchema, [outSchema, curConfig]);
  const langLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    curLangOptions.forEach((item) => {
      const value = `${item?.value ?? ''}`;
      const label = item?.label;
      if (value && typeof label === 'string') {
        map[value] = label;
      }
    });
    return map;
  }, [curLangOptions]);

  /** 按只读状态选择语言页签中的编辑或展示内容。 */
  const ItemContent = useCallback((props: IAnyObject) => {
    if (readOnly) {
      if (OutContent) {
        return <OutContent {...props} />;
      }
      if (curI18nOutSchema) {
        return <I18nOutContent schema={curI18nOutSchema} {...props} />;
      }
      return null;
    }

    if (InContent) {
      return <InContent {...props} />;
    }

    if (curI18nInSchema) {
      return <I18nInContent schema={curI18nInSchema} {...props} />;
    }
  }, [curI18nInSchema, curI18nOutSchema, InContent, OutContent, readOnly]);

  const [i18nTargetFields, fieldReverseMap] = useMemo(() => {
    const i18nTargetFields = Object.keys(curI18nFieldMap);
    const reverseMap: Record<string, string> = {};
    Object.entries(curI18nFieldMap).forEach(([key, value]) => {
      reverseMap[value] = key;
    });

    return [i18nTargetFields, reverseMap];
  }, [curI18nFieldMap]);

  useEffect(() => {
    const dispose = autorun(() => {
      const arr: string[] = [];
      i18nTargetFields.forEach((field) => {
        const keys = Object.keys(values?.[field] || {});
        keys.forEach((key) => {
          langStore.setFieldValue(`${key}.${curI18nFieldMap[field]}`, values?.[field]?.[key]);
        });
        arr.push(...keys);
      });
      const unionArr = union(arr);
      setLangs(unionArr);
    });
    return () => {
      dispose();
    };
  }, [curI18nFieldMap, i18nTargetFields, langStore, values]);

  if (isVacuous(curStore)) {
    return null;
  }

  return (
    <Drawer
      footer
      bindChildStore={false}
      okButtonProps={{
        children: locale.confirm,
        disabled: readOnly,
        onClick: () => {
          // 只读抽屉不仅隐藏输入，还必须阻断确认按钮对宿主 Store 的写回。
          if (readOnly) return;
          const nextI18nValues: Record<string, IAnyObject> = {};
          i18nTargetFields.forEach((field) => {
            nextI18nValues[field] = {};
          });
          langs?.forEach((lang) => {
            const value = langStore.values[lang] ?? {};
            const keys = Object.keys(value);
            keys.forEach((sourceKey) => {
              const targetKey = fieldReverseMap[sourceKey];
              if (!targetKey) {
                return;
              }
              nextI18nValues[targetKey] = nextI18nValues[targetKey] || {};
              nextI18nValues[targetKey][lang] = value[sourceKey];
            });
          });
          i18nTargetFields.forEach((field) => {
            curStore?.setFieldValue(field, nextI18nValues[field] ?? {});
          });
        },
      }}
      title={locale.title}
      trigger={{ type: 'primary' }}
      width="min(100vw, 1000px)">
      <div>
        {!readOnly && <Alert closable
          showIcon
          message={locale.help}
          style={{ marginBottom: token.marginSM }}
          type="warning" />}
        <Modal
          open={isAddLang}
          title={locale.addLanguage}
          width={`min(360px, calc(100vw - ${token.marginLG * 2}px))`}
          onCancel={() => {
            setIsAddLang(false);
          }}
          onOk={() => {
            if (isVacuous(addLang)) {
              notification.error({ message: locale.selectLanguage });
              return false;
            }
            if (langs.includes(addLang)) {
              notification.error({ message: locale.languageExists });
              return false;
            }
            setLangs([...langs, addLang]);
            const df: IAnyObject = {};
            i18nTargetFields.forEach((field) => {
              const key = curI18nFieldMap[field];
              df[`${key}`] = curConfig?.defaultValues?.[key] ?? undefined;
            });
            langStore.setValues({ [addLang]: df });
            setIsAddLang(false);
            setAddLang('');
          }} >
          <Select
            showSearch
            optionFilterProp="label"
            options={curLangOptions?.filter(item => !langs.includes(item.value))}
            style={{ width: '100%' }}
            value={addLang}
            onChange={setAddLang}
          />
        </Modal>
        <Tabs
          destroyOnHidden
          items={langs.map(lang => ({
            key: lang,
            label: langLabelMap[lang] ?? lang,
            children: readOnly
              ? <ItemContent key={lang} {...outProps} value={langStore.values[lang]} />
              : <ItemContent
                key={lang}
                {...inProps}
                value={langStore.values[lang]}
                onChange={(v: IAny) => {
                  langStore.setValues({ [lang]: v });
                }}
              />
            ,
          }))}
          type={readOnly ? 'card' : 'editable-card'}
          onEdit={(key, action) => {
            if (action === 'add') {
              setIsAddLang(true);
              return;
            }
            if (action === 'remove' && typeof key === 'string') {
              setLangs(langs.filter(lang => lang !== key));
              delete langStore.values[key];
              return;
            }
          }}
        />
      </div>
    </Drawer >
  );
});

/** 从显式配置或默认字典接口加载多语言选项。 */
const useI18nLangOptions = (langAPI?: IStoreAPI, config?: IEntityConfig) => {
  const curLangAPI = useMemo(
    () => normalizeI18nLangAPI(langAPI ?? config?.i18nLangAPI ?? getDefaultI18nLangAPI()),
    [langAPI, config],
  );
  const langOptionsStore = useInitStore<IAnyObject, { options?: IOptions }>({
    api: curLangAPI,
    options: { runNow: curLangAPI ? 'always' : 'never' },
  });
  const remoteLangOptions = getRemoteLangOptions(langOptionsStore.response);
  if (remoteLangOptions) {
    return remoteLangOptions;
  }
  const hasFetched = typeof langOptionsStore.response?.code !== 'undefined';
  // 仅使用接口数据，不再回退本地写死 map
  if (curLangAPI && hasFetched) return [];
  return [];
};

const I18N_LANGUAGE_OPTIONS_NAME = 'i18nLanguageOptions';

/** 为对象形式的语言接口补充默认字典名称，同时保留函数执行器。 */
const normalizeI18nLangAPI = (api?: IStoreAPI) => {
  if (!api || typeof api === 'function') {
    return api;
  }
  return {
    ...api,
    params: {
      ...(api.params ?? {}),
      name: api.params?.name ?? I18N_LANGUAGE_OPTIONS_NAME,
    },
  };
};

/** 兼容现有字典接口的响应包裹层并提取语言选项。 */
const getRemoteLangOptions = (response?: IAnyObject): IOptions | undefined => {
  const maybeOptions = [
    response?.data?.options,
    response?.data?.data?.options,
    response?.data?.value?.options,
    response?.data,
    response?.options,
  ];
  for (const item of maybeOptions) {
    const parsedOptions = parseLangOptions(item);
    if (parsedOptions) return parsedOptions;
  }
  return undefined;
};

/** 把数组或 JSON 字符串形式的字典值解析为语言选项。 */
const parseLangOptions = (item: unknown): IOptions | undefined => {
  if (Array.isArray(item)) {
    return item as IOptions;
  }
  if (typeof item !== 'string') {
    return undefined;
  }
  try {
    const parsed = JSON.parse(item);
    if (Array.isArray(parsed)) {
      return parsed as IOptions;
    }
  } catch {
    // ignore parse error
  }
  return undefined;
};

/** 根据当前管理端路径选择已有字典查询入口。 */
const getDefaultI18nLangAPI = (): IStoreAPI | undefined => ({
  // admin 端走 options，其他（含 platform 端）走 getCacheByName
  url: isAdminPath() ? I18N_LANGUAGE_OPTIONS_API_URL : I18N_LANGUAGE_CACHE_API_URL,
});

/** 判断当前页面是否处于租户管理端路由。 */
const isAdminPath = () => /(^|\/)admin(\/|$)/.test(window.location.pathname);

const I18N_LANGUAGE_OPTIONS_API_URL = '/base/iam/portal/dictionary/options';
const I18N_LANGUAGE_CACHE_API_URL = '/base/iam/manage/dictionary/getCacheByName';

interface I18nInContentProps {
  value?: IAnyObject;
  onChange?: (v?: IAnyObject) => void;
  schema?: ISchema;
  config?: IEntityConfig;
}

/** 在单语言页签内复用 Entity 表单并同步编辑值。 */
const I18nInContent = observer((props: I18nInContentProps) => {
  const { value, onChange, schema, config } = props;
  const curConfig = useEntityConfig(config);
  const cStore = useInitStore({ defaultValues: value ?? {}, fieldsConfig: curConfig?.fieldsConfig, formConfig: {} });

  useEffect(() => {
    const dispose = autorun(() => {
      const values = cStore?.values ?? {};
      onChange?.({ ...values });
    });
    return () => {
      dispose();
    };
  }, [cStore.values, onChange]);

  useDeepEffect(() => {
    cStore.setDefaultValues(value ?? {});
  }, [cStore, value]);

  return (
    <Entity schema={schema} store={cStore} />
  );
});

interface I18nOutContentProps {
  value?: IAnyObject;
  schema?: ISchema;
  config?: IEntityConfig;
}

/** 在只读页签内复用 Entity 输出 Schema 展示语言值。 */
const I18nOutContent = observer((props: I18nOutContentProps) => {
  const { value, schema, config } = props;
  const curConfig = useEntityConfig(config);
  const cStore = useInitStore({ defaultValues: value, fieldsConfig: curConfig?.fieldsConfig });

  useDeepEffect(() => {
    cStore.setDefaultValues(value ?? {});
  }, [cStore, value]);

  return (
    <Entity schema={schema} store={cStore} />
  );
});
